import { useCallback, useEffect, useRef } from 'react'
import { useUpdateMarkingFormData, useGetMarkingFormData } from '@/shared/api'
import type { WorkspaceWithLinkDescriptor } from '@synapex/shared2/entities'
import type { MarkingEditorState } from '../types'
import {
    loadFromLocalStorage,
    clearLocalStorage,
    createMarkingFormDataDto,
    type MarkingEditorScale,
} from '../utils'
import { migrateOldMarkingData } from '../utils/persistence/migrationUtils'
import type { UpdateMarkingFormDataInputDto } from '@synapex/shared2/gateway-rest-api'
import { useQueryClient } from '@tanstack/react-query'
import { apiQueryKey } from '@/shared/api'

// Реэкспорт типа для обратной совместимости
export type { MarkingEditorScale } from '../utils'

/**
 * Хук для работы с сохранением и загрузкой настроек маркировки
 * Все данные сохраняются в БД (SYN-829)
 */
export function useMarkingPersistence(
    wwld: WorkspaceWithLinkDescriptor,
    isOpen?: boolean,
    options?: { enabled?: boolean }
) {
    const queryClient = useQueryClient()
    const {
        data: savedData,
        isLoading,
        isFetching,
        refetch,
    } = useGetMarkingFormData(wwld, {
        enabled: options?.enabled ?? true,
    })
    const updateMarkingFormData = useUpdateMarkingFormData(wwld)
    const prevIsOpenRef = useRef(isOpen)
    const dataVersionRef = useRef(0)

    // Принудительно обновляем данные при открытии редактора
    // Это гарантирует загрузку настроек из CreativeMarkingSettings для новых ссылок
    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            // Редактор только что открылся - принудительно обновляем данные
            // Инвалидируем кэш и делаем refetch, чтобы загрузить настройки из CreativeMarkingSettings
            queryClient.invalidateQueries({
                queryKey: apiQueryKey.markingFormData(wwld),
            })
            // Используем refetch из useQuery для обновления данных
            refetch()
        }
        prevIsOpenRef.current = isOpen
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen])

    // Отслеживаем изменения данных для сброса состояния редактора
    useEffect(() => {
        if (savedData && !isFetching) {
            dataVersionRef.current += 1
        }
    }, [savedData, isFetching])

    /**
     * Сохранение всех параметров маркировки в БД
     */
    const saveMarkingSettings = useCallback(
        (
            state: MarkingEditorState,
            scale?: MarkingEditorScale | null,
            customTextColors?: string[],
            customBackgroundColors?: string[]
        ) => {
            const dto = createMarkingFormDataDto(
                state,
                scale,
                customTextColors,
                customBackgroundColors
            )

            // Сохраняем все поля в БД
            // createMarkingFormDataDto уже возвращает правильный тип
            // Удаляем данные из localStorage только после успешного сохранения в БД
            updateMarkingFormData.mutate(dto, {
                onSuccess: () => {
                    // Удаляем данные из localStorage после успешной миграции в БД
            clearLocalStorage(wwld)
                },
            })
        },
        [updateMarkingFormData, wwld]
    )

    /**
     * Загрузка сохраненных настроек из БД
     * Также проверяет localStorage для миграции старых данных
     */
    const loadMarkingSettings = useCallback(() => {
        // Загружаем данные из БД
        const migratedFromDb = savedData
            ? migrateOldMarkingData(savedData)
            : null

        // Загружаем расширенные данные из localStorage (для миграции старых данных)
        const localData = loadFromLocalStorage(wwld)

        // Объединяем данные: приоритет у данных из БД, затем из localStorage
        if (migratedFromDb || localData) {
            return {
                ...localData,
                ...migratedFromDb, // Данные из БД имеют приоритет
            }
        }

        return null
    }, [savedData, wwld])

    return {
        saveMarkingSettings,
        loadMarkingSettings,
        savedData,
        isLoading,
        isFetching,
        isSaving: updateMarkingFormData.isPending,
        dataVersion: dataVersionRef.current,
    }
}

export type UseMarkingPersistenceReturn = ReturnType<
    typeof useMarkingPersistence
>
